import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import ReactDOM from "react-dom";

const HIDE_DELAY_MS = 150;

type TooltipState = {
  visible: boolean;
  x: number;
  y: number;
  content: React.ReactNode | null;
};

type TooltipContextValue = {
  showTooltip: (opts: { x: number; y: number; content: React.ReactNode }) => void;
  scheduleShowTooltip: (opts: { x: number; y: number; content: React.ReactNode }, delayMs: number) => void;
  hideTooltip: () => void;
  scheduleDelayedHide: (delayMs?: number) => void;
  cancelDelayedHide: () => void;
};

const TooltipContext = createContext<TooltipContextValue | null>(null);

export function useTooltipController(): TooltipContextValue {
  const ctx = useContext(TooltipContext);
  if (!ctx) {
    throw new Error("useTooltipController must be used within a TooltipProvider");
  }
  return ctx;
}

export function TooltipProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [state, setState] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });

  const showTooltip = useCallback((opts: { x: number; y: number; content: React.ReactNode }) => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setState({
      visible: true,
      x: opts.x,
      y: opts.y,
      content: opts.content,
    });
  }, []);

  const scheduleShowTooltip = useCallback(
    (opts: { x: number; y: number; content: React.ReactNode }, delayMs: number) => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = setTimeout(() => {
        showTimeoutRef.current = null;
        showTooltip(opts);
      }, delayMs);
    },
    [showTooltip],
  );

  const hideTooltip = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      visible: false,
    }));
  }, []);

  const scheduleDelayedHide = useCallback((delayMs: number = HIDE_DELAY_MS) => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      hideTimeoutRef.current = null;
      setState((prev) => ({ ...prev, visible: false }));
    }, delayMs);
  }, []);

  const cancelDelayedHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  return (
    <TooltipContext.Provider
      value={{ showTooltip, scheduleShowTooltip, hideTooltip, scheduleDelayedHide, cancelDelayedHide }}
    >
      {children}
      {state.visible &&
        ReactDOM.createPortal(
          <div
            className="App tooltip_V2_host"
            style={{
              left: state.x,
              top: state.y,
            }}
            onMouseEnter={cancelDelayedHide}
            onMouseLeave={hideTooltip}
          >
            {state.content}
          </div>,
          document.getElementById("root") ?? document.body,
        )}
    </TooltipContext.Provider>
  );
}
